import { useMemo } from 'react';
import { t } from '@aquabox/shared';
import { useHayatVehicles } from '../api/queries';
import type { HayatVehiclesResponse } from '../api/types';

/** Tabloda tek satır: Abant çalışanı + gözlemlenen sipariş sayısı. */
interface AbantEmployeeRow {
  /** Abant çalışan kimliği (`String(dealerEmployeeId)`) — `Order.externalVehicleRaw` ile aynı. */
  label: string;
  /** "Ad Soyad" — canlı listeden ya da `employeeNames`ten; ikisi de yoksa ham kimlik. */
  displayName: string;
  isPassive: boolean;
  orderCount: number;
}

/**
 * `observed` (bizim siparişlerde görülen çalışan kimlikleri) ve `live` (canlı çalışan listesi)
 * birleşir. Ad önceliği: canlı liste (`live[].displayName`) > kayıtlı `employeeNames` > ham kimlik.
 * Araç eşlemesi (`map`) bilerek OKUNMAZ: Abant'ta araç artık çalışan → şoför → roster ile çözülür.
 */
function buildAbantEmployeeRows(data: HayatVehiclesResponse | undefined): AbantEmployeeRow[] {
  if (!data) return [];
  const nameFor = (label: string) => data.employeeNames?.[label] ?? label;
  const byLabel = new Map<string, AbantEmployeeRow>();
  for (const o of data.observed ?? []) {
    byLabel.set(o.label, {
      label: o.label,
      displayName: nameFor(o.label),
      isPassive: false,
      orderCount: o.orderCount,
    });
  }
  for (const v of data.live ?? []) {
    const existing = byLabel.get(v.label);
    if (existing) {
      existing.displayName = v.displayName ?? existing.displayName;
      existing.isPassive = v.isPassive ?? existing.isPassive;
    } else {
      byLabel.set(v.label, {
        label: v.label,
        displayName: v.displayName ?? v.label,
        isPassive: v.isPassive ?? false,
        orderCount: 0,
      });
    }
  }
  return [...byLabel.values()].sort(
    (a, b) => b.orderCount - a.orderCount || a.displayName.localeCompare(b.displayName, 'tr'),
  );
}

/**
 * "Abant çalışanları" — yalnız AD GÖSTERİMİ (salt okunur).
 *
 * Önceki araç sütunu (vehicleMap yazımı) kaldırıldı: Abant'ta etiket artık şoföre (Panel ataması,
 * driverMap) ve oradan araca roster ile bağlanır; vehicleMap Abant için ölü veri. Bu kart ad
 * BESLEMEZ: `employeeNames` şoför eşlemesi kaydıyla (driverMap PUT, API d4561ab9) yazılır; kart
 * yalnız o kaydı OKUR ve listeler.
 */
export function AbantEmployeesCard() {
  const data = useHayatVehicles('ABANT_PANEL');
  const rows = useMemo(() => buildAbantEmployeeRows(data.data), [data.data]);

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('abantEmp.title')}</strong>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('abantEmp.nameHint')}
      </p>
      {data.isLoading ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('abantEmp.unmapped')}</p>
      ) : (
        <table
          data-testid="abant-employee-table"
          style={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <thead>
            <tr style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>{t('abantEmp.colName')}</th>
              <th style={{ padding: '6px 8px' }}>{t('abantEmp.colCount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} data-testid={`abant-emp-row-${row.label}`}>
                <td style={{ padding: '6px 8px', fontSize: 13.5 }}>
                  {row.displayName}
                  {row.isPassive ? (
                    <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                      {' '}
                      ({t('abantEmp.passive')})
                    </span>
                  ) : null}
                </td>
                <td
                  style={{
                    padding: '6px 8px',
                    fontSize: 13.5,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {row.orderCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
